import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LayoutGrid, Shield, Sparkles } from "lucide-react";
import { OmniLogo } from "./OmniLogo";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const APPS = [
  { brand: "search", label: "Recherche", to: "/", testid: "app-search" },
  { brand: "mail", label: "OmniMail", to: "/mail", testid: "app-mail" },
  { brand: "tube", label: "OmniTube", to: "/tube", testid: "app-tube" },
  { brand: "ai", label: "OmniAI", to: "/ai", testid: "app-ai" },
  { brand: "drive", label: "OmniDrive", to: "/drive", testid: "app-drive" },
  { brand: "map", label: "OmniMap", to: "/map", testid: "app-map" },
];

const EXTRA = [
  { to: "/store", testid: "app-store", letter: "S", color: "#7c3aed", label: "OmniStore" },
  { to: "/account", testid: "app-account", letter: "A", color: "#3b82f6", label: "Compte" },
  { to: "/bookmarks", testid: "app-bookmarks", letter: "★", color: "#f59e0b", label: "Favoris" },
  { to: "/translate", testid: "app-translate", letter: "Aa", color: "#0ea5e9", label: "Traduire" },
];

// Behaviour map for each installed extension when clicked from the apps grid.
// Routes existing pages where the feature is already implemented, or shows a
// playful toast for purely-decorative ones so the user feels they did something.
const EXTENSION_BEHAVIOUR = {
  "dark-mode": () => { window.location.href = "/store?c=theme"; },
  "ad-block-plus": () => { toast.success("🛡️ OmniBlock activé — trackers et bannières bloqués sur les pages proxifiées."); },
  "pwd-vault": () => { window.location.href = "/passwords"; },
  "translator": () => { window.location.href = "/translate"; },
  "screenshot-pro": async () => {
    try {
      const canvas = await (await import("html2canvas")).default(document.body);
      const a = document.createElement("a");
      a.href = canvas.toDataURL();
      a.download = `omnisearch-${Date.now()}.png`;
      a.click();
    } catch {
      // html2canvas not installed → fallback to plain message
      toast.success("📸 Capture d'écran (Ctrl+Maj+S pour la version native du navigateur).");
    }
  },
  "tab-saver": () => {
    const tabs = [window.location.pathname];
    localStorage.setItem("omni:saved-tabs", JSON.stringify({ tabs, at: Date.now() }));
    toast.success("💾 Onglet sauvegardé. Restaurez depuis cette même action.");
  },
};

// Module-level cache: the OmniStore catalog never changes between renders.
let _catalogCache = null;

export function AppsMenu() {
  const { user } = useAuth() || {};
  const isAdmin = user && user !== false && user.role === "admin";
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(() => {
    // Optimistic read from localStorage so the badge appears instantly on nav
    try {
      const cached = JSON.parse(localStorage.getItem("omni:store:installed") || "[]");
      return Array.isArray(cached) ? cached : [];
    } catch { return []; }
  });
  const [catalog, setCatalog] = useState(_catalogCache || []);
  const navigate = useNavigate();
  const fetchedInstalled = useRef(false);

  // Fetch catalog ONCE per session (module-level cache).
  useEffect(() => {
    if (_catalogCache) return;
    api.get("/store/items").then(({ data }) => {
      _catalogCache = data.items || [];
      setCatalog(_catalogCache);
    }).catch(() => {});
  }, []);

  // Lazy-fetch installed extensions ONLY when the menu is first opened.
  useEffect(() => {
    if (!open || fetchedInstalled.current) return;
    if (!user || user === false) { setInstalled([]); return; }
    fetchedInstalled.current = true;
    api.get("/store/installed").then(({ data }) => {
      const list = data.installed || [];
      setInstalled(list);
      try { localStorage.setItem("omni:store:installed", JSON.stringify(list)); } catch {}
    }).catch(() => {});
  }, [open, user]);

  // Only show installed *extensions*, not themes (themes are activated from /store)
  const installedExtensions = catalog.filter((it) => it.category === "extension" && installed.includes(it.id));

  const onExtClick = (e, ext) => {
    e.preventDefault();
    const fn = EXTENSION_BEHAVIOUR[ext.id];
    if (fn) {
      fn();
    } else {
      navigate(`/store?ext=${ext.id}`);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="apps-menu-trigger"
          aria-label="Applications OmniSearch"
          className="p-2 rounded-full hover:bg-gray-100 transition relative"
        >
          <LayoutGrid size={20} />
          {installedExtensions.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-[var(--omni-accent)] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="apps-extensions-badge">
              {installedExtensions.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-3 max-h-[80vh] overflow-y-auto" data-testid="apps-menu">
        <div className="px-2 pb-2 pt-1 text-xs text-gray-500">Applications Omni</div>
        <div className="grid grid-cols-3 gap-1">
          {APPS.map(({ brand, label, to, testid }) => (
            <Link
              key={brand}
              to={to}
              data-testid={testid}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[var(--omni-surface)] transition group"
            >
              <span className="group-hover:scale-105 transition">
                <OmniLogo brand={brand} size={44} />
              </span>
              <span className="text-xs text-gray-700 text-center leading-tight">{label}</span>
            </Link>
          ))}
          {EXTRA.map((e) => (
            <Link
              key={e.testid}
              to={e.to}
              data-testid={e.testid}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[var(--omni-surface)] transition group"
            >
              <span
                className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold text-sm group-hover:scale-105 transition"
                style={{ background: e.color }}
              >
                {e.letter}
              </span>
              <span className="text-xs text-gray-700 text-center leading-tight">{e.label}</span>
            </Link>
          ))}
        </div>

        {/* Installed extensions section */}
        {installedExtensions.length > 0 && (
          <>
            <div className="border-t border-[var(--omni-border)] mt-3 pt-2 pb-1 px-2 flex items-center gap-1.5 text-xs text-gray-500">
              <Sparkles size={11} className="text-[var(--omni-accent)]" />
              <span>Mes extensions ({installedExtensions.length})</span>
            </div>
            <div className="grid grid-cols-3 gap-1" data-testid="apps-installed-extensions">
              {installedExtensions.map((ext) => (
                <a
                  key={ext.id}
                  href={`/store?ext=${ext.id}`}
                  onClick={(e) => onExtClick(e, ext)}
                  data-testid={`app-ext-${ext.id}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[var(--omni-surface)] transition group relative"
                  title={ext.description}
                >
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center text-2xl group-hover:scale-105 transition"
                    style={{ background: `linear-gradient(135deg, ${ext.color}50, ${ext.color}20)` }}
                  >
                    {ext.icon}
                  </span>
                  <span className="text-xs text-gray-700 text-center leading-tight truncate w-full">{ext.name}</span>
                </a>
              ))}
            </div>
          </>
        )}

        {/* Empty state encourager */}
        {user && user !== false && installedExtensions.length === 0 && (
          <Link
            to="/store"
            data-testid="apps-discover-extensions"
            className="block mt-3 border-t border-[var(--omni-border)] pt-3 px-3 py-2 text-xs text-gray-500 hover:bg-[var(--omni-surface)] rounded-lg"
          >
            <Sparkles size={11} className="inline text-[var(--omni-accent)] mr-1" />
            Installez des extensions depuis l'OmniStore →
          </Link>
        )}

        {isAdmin && (
          <Link
            to="/admin"
            data-testid="app-admin"
            className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-[var(--omni-surface)] transition group border-t border-[var(--omni-border)] mt-1 pt-3"
          >
            <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-white group-hover:scale-105 transition bg-gradient-to-br from-zinc-900 to-zinc-700">
              <Shield size={20} />
            </span>
            <span className="text-xs text-gray-700 text-center leading-tight font-semibold">Panel Admin</span>
          </Link>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
